import { FC, useState } from "react"
import { GenericMessage } from "components/feedback/Message";
import { Transition } from "@headlessui/react";
import { XMarkIcon, CheckIcon, EditIcon } from "components/icons/Icons";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { NsKey } from "enums/NsKey";

interface FunnelCardPercentageProps {
  onSelect: () => void;
  percentage: number;
  missingGuarantees?: string[];
  warningGuaranteesMessages?: string[];
}

export const FunnelCardPercentage: FC<FunnelCardPercentageProps> = ({
  onSelect,
  percentage,
  missingGuarantees = [],
  warningGuaranteesMessages = []
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const { t } = useDynamicTranslation([NsKey.Pricing]);

  const isComplete = percentage === 100;
  const messageType = isComplete && !(warningGuaranteesMessages.length > 0) ? "success" : "warning";

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsTooltipOpen(true)}
      onMouseLeave={() => setIsTooltipOpen(false)}
    >
      <GenericMessage
        size="sm"
        message={t('PRICING.FUNNEL_CARD.MATCHES_PERCENTAGE', { percentage })}
        dismissible={false}
        type={messageType}
        actionButton={
          {
            icon: EditIcon,
            onClick: () => onSelect()
          }
        }
      />
      <div className="h-1" />
      <Transition show={isTooltipOpen}>
        <div className="absolute text-xs top-full left-0 z-50 w-full bg-zinc-50 border border-gray-200 rounded-lg shadow-lg p-3 transition duration-300 ease-in data-closed:opacity-0 data-closed:scale-95">
          {isComplete && !(warningGuaranteesMessages.length > 0) &&
            <div className="flex items-stretch gap-2">
              <div className="flex-none mt-0.5 w-3 h-3 rounded-full border border-primary flex items-center justify-center">
                <CheckIcon className="text-primary w-3 h-3" />
              </div>
              <p className="font">{t('PRICING.FUNNEL_CARD.INCLUDES_ALL_OPTIONS')}</p>
            </div>
          }
          {
            missingGuarantees.length > 0 &&
            <>
              <p className="font-bold mb-2">{t('PRICING.FUNNEL_CARD.NOT_INCLUDED')}</p>
              {missingGuarantees.map((guarantee, index) => (
                <div key={index} className="flex items-stretch gap-2 mb-1 last:mb-0">
                  <div className="flex-none w-3 h-3 mt-0.5 rounded-full border border-secondary">
                    <XMarkIcon className="flex-wrap text-secondary w-full" />
                  </div>
                  <p>{guarantee}</p>
                </div>
              ))}
            </>
          }
          {
            warningGuaranteesMessages.length > 0 &&
            <>
              <p className="font-bold mb-2">{t('PRICING.FUNNEL_CARD.GUARANTEE_ADJUSTMENTS')}</p>
              {warningGuaranteesMessages.map((guarantee, index) => (
                <div key={index} className="flex items-stretch gap-2 mb-1 last:mb-0">
                  <div className="flex-none w-3 h-3 mt-0.5 rounded-full border border-secondary">
                    <XMarkIcon className="flex-wrap text-secondary w-full" />
                  </div>
                  <p>{guarantee}</p>
                </div>
              ))}
            </>
          }

        </div>
      </Transition>
    </div>
  );
};
